// src/components/Posts/CalendarVoteForm.jsx
import {
    Box, Typography, TextField, Button, IconButton,
    Switch, FormControlLabel, Stack, Divider, Card, CardContent,
    Grid, Collapse
} from '@mui/material';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';

const VoteForm = ({ onChange, isEdit = false, initialVoteData = null }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [openVoteForm, setOpenVoteForm] = useState(isMobile);
    const [voteQuestion, setVoteQuestion] = useState('');
    const [voteOptions, setVoteOptions] = useState(['', '']);
    const [voteDate, setVoteDate] = useState(dayjs().add(3, 'day'));
    const [voteTime, setVoteTime] = useState(dayjs().hour(23).minute(59));
    const [voteDeadline, setVoteDeadline] = useState(dayjs().add(3, 'day').hour(23).minute(59));
    const [allowMultiple, setAllowMultiple] = useState(false);
    const [isAnonymousVote, setIsAnonymousVote] = useState(false);
    const [hasVoteRecord, setHasVoteRecord] = useState(false);

    const lastSentData = useRef('');
    const didInit = useRef(false);

    useEffect(() => {
        if (isMobile) setOpenVoteForm(true);
    }, [isMobile]);

    useEffect(() => {
        if (!didInit.current && initialVoteData) {
            didInit.current = true;

            setOpenVoteForm(true);
            setVoteQuestion(initialVoteData.vote_question || '');
            setVoteOptions(initialVoteData.vote_options || ['', '']);

            const initDeadline = initialVoteData.vote_deadline
                ? dayjs(initialVoteData.vote_deadline)
                : dayjs().add(3, 'day').hour(23).minute(59);

            setVoteDate(initDeadline.startOf('day'));
            setVoteTime(initDeadline);
            setVoteDeadline(initDeadline);
            setAllowMultiple(initialVoteData.allow_multiple || false);
            setIsAnonymousVote(initialVoteData.is_anonymous_vote ?? true);
            setHasVoteRecord(initialVoteData.has_vote_record || false);
        }
    }, [initialVoteData]);

    useEffect(() => {
        if (openVoteForm && voteDate && voteTime) {
            const combined = voteDate
                .hour(voteTime.hour())
                .minute(voteTime.minute())
                .second(0);
            setVoteDeadline(combined);
        }
    }, [voteDate, voteTime, openVoteForm]);

    useEffect(() => {
        if (!openVoteForm) {
            if (lastSentData.current !== 'null') {
                lastSentData.current = 'null';
                onChange(null);
            }
            return;
        }

        const trimmedOptions = voteOptions.map(o => o.trim()).filter(o => o);
        const currentData = JSON.stringify({
            has_vote: true,
            vote_question: voteQuestion.trim(),
            vote_options: trimmedOptions,
            vote_deadline: voteDeadline.format('YYYY-MM-DD HH:mm:ss'),
            allow_multiple: allowMultiple,
            is_anonymous_vote: isAnonymousVote,
        });

        if (lastSentData.current !== currentData) {
            lastSentData.current = currentData;
            onChange(JSON.parse(currentData));
        }
    }, [
        openVoteForm,
        voteQuestion,
        voteOptions,
        voteDeadline,
        allowMultiple,
        isAnonymousVote,
        onChange
    ]);

    const handleOptionChange = (idx, value) => {
        const updated = [...voteOptions];
        updated[idx] = value;
        setVoteOptions(updated);
    };

    const addOption = () => {
        if (voteOptions.length >= 10) {
            alert('선택지는 최대 10개까지 가능합니다.');
            return;
        }
        setVoteOptions([...voteOptions, '']);
    };

    const removeOption = idx => {
        if (hasVoteRecord) return;
        if (voteOptions.length <= 2) {
            alert('선택지는 최소 2개 이상이어야 합니다.');
            return;
        }
        const updated = [...voteOptions];
        updated.splice(idx, 1);
        setVoteOptions(updated);
    };

    return (
        <Box sx={{ mt: 4 }}>
            {!isMobile && (
                <Box sx={{ textAlign: 'left', mb: 2 }}>
                    <Button
                        variant={openVoteForm ? 'contained' : 'outlined'}
                        startIcon={<HowToVoteIcon />}
                        endIcon={
                            <ExpandMoreIcon
                                sx={{
                                    transform: openVoteForm ? 'rotate(180deg)' : 'none',
                                    transition: '.2s',
                                }}
                            />
                        }
                        onClick={() => setOpenVoteForm(!openVoteForm)}
                    >
                        {openVoteForm ? '투표 접기' : '투표 추가하기'}
                    </Button>
                </Box>
            )}

            <Collapse in={openVoteForm} timeout="auto" unmountOnExit>
                <Card variant="outlined" sx={{ mt: isMobile ? 0 : 1 }}>
                    <CardContent>
                        <Stack spacing={3}>
                            {hasVoteRecord && (
                                <Typography variant="body2" color="text.secondary">
                                    ❗ 이미 투표자가 있어 마감 날짜 외 설정은 수정할 수 없습니다.
                                </Typography>
                            )}

                            {!hasVoteRecord && (
                                <>
                                    <TextField
                                        fullWidth
                                        label="투표 질문"
                                        value={voteQuestion}
                                        onChange={e => setVoteQuestion(e.target.value)}
                                    />
                                    <Box>
                                        <Typography variant="subtitle2" gutterBottom>
                                            선택지
                                        </Typography>
                                        <Stack spacing={1}>
                                            {voteOptions.map((opt, idx) => (
                                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <TextField
                                                        fullWidth
                                                        label={`선택지 ${idx + 1}`}
                                                        value={opt}
                                                        onChange={e => handleOptionChange(idx, e.target.value)}
                                                    />
                                                    {voteOptions.length > 2 && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => removeOption(idx)}
                                                            color="error"
                                                            sx={{ width: 28, height: 28 }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ))}
                                            <Button
                                                variant="outlined"
                                                startIcon={<AddIcon />}
                                                onClick={addOption}
                                                sx={{ mt: 1 }}
                                            >
                                                선택지 추가
                                            </Button>
                                        </Stack>
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={allowMultiple}
                                                        onChange={e => setAllowMultiple(e.target.checked)}
                                                    />
                                                }
                                                label="복수 선택 허용"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={isAnonymousVote}
                                                        onChange={e => setIsAnonymousVote(e.target.checked)}
                                                    />
                                                }
                                                label="익명 투표"
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider />
                                </>
                            )}

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <DatePicker
                                            label="마감 날짜"
                                            value={voteDate}
                                            onChange={newVal => {
                                                const combined = newVal
                                                    ?.hour(voteTime.hour())
                                                    .minute(voteTime.minute())
                                                    .second(0);

                                                if (combined.isBefore(dayjs())) {
                                                    alert('⚠️ 마감 시각은 현재 시각보다 이후여야 합니다.');
                                                    return;
                                                }
                                                setVoteDate(newVal);
                                            }}
                                            minDate={dayjs().startOf('day')}
                                            format="YYYY/MM/DD"
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TimePicker
                                            label="마감 시간"
                                            value={voteTime}
                                            onChange={newVal => {
                                                const combined = voteDate
                                                    ?.hour(newVal.hour())
                                                    .minute(newVal.minute())
                                                    .second(0);

                                                if (combined.isBefore(dayjs())) {
                                                    alert('⚠️ 마감 시각은 현재 시각보다 이후여야 합니다.');
                                                    return;
                                                }
                                                setVoteTime(newVal);
                                            }}
                                            ampm
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </Grid>
                                </Grid>
                            </LocalizationProvider>
                        </Stack>
                    </CardContent>
                </Card>
            </Collapse>
        </Box>
    );
};

export default VoteForm;
